// useSmsEditor — Flow Editor state, ported from the prototype's sms-editor.js
// Alpine module to a Vue Composition API composable. Pure reactive state; the
// one DOM touch (cursor-aware variable insert) uses a template ref.
import { ref, computed, nextTick } from "vue";
import * as SMS from "../sms-config";

export function useSmsEditor() {
  // ── Editor state ──────────────────────────────────────────────────────
  const msgType = ref("heads-up");
  const currentVar = ref({
    building: "House",
    timing: "Morning",
    weather: "Perfect"
  });
  const msgContent = ref(SMS.VARIATION_CONTENT[SMS.DEFAULT_VARIATION_KEY]);
  const openGroups = ref({
    "grp-house": true,
    "grp-town": true,
    "grp-apt": true,
    "grp-biz": true,
    "grp-hosp": true
  });
  const editorSaved = ref(false);
  const editorCopied = ref(false);
  const editorTextarea = ref(null); // bound via ref="editorTextarea" in the template

  // ── Reference data (read-only passthroughs) ───────────────────────────
  const messageTypes = SMS.MESSAGE_TYPES;
  const buildings = SMS.BUILDINGS;
  const editorVars = SMS.EDITOR_VARS;
  const editorVersions = SMS.EDITOR_VERSIONS;
  const variationsFor = building => SMS.buildVariations(building);

  // ── Derived editor state ──────────────────────────────────────────────
  const currentMsgType = computed(
    () =>
      SMS.MESSAGE_TYPES.find(t => t.key === msgType.value) ||
      SMS.MESSAGE_TYPES[0]
  );
  const hasVariations = computed(() => currentMsgType.value.hasVariations);
  const editorTitle = computed(() => currentMsgType.value.editorTitle);

  const variationTitle = computed(() => {
    if (!hasVariations.value) return "Single message — no variations";
    const v = currentVar.value;
    return `${v.building} · ${v.timing} · ${v.weather} weather`;
  });

  const draftLabel = computed(() => {
    const v = currentVar.value;
    const label = hasVariations.value
      ? `${v.building} / ${v.timing} / ${v.weather}`
      : currentMsgType.value.label;
    return `Unsaved draft — Callia Driver · ${currentMsgType.value.label} · ${label}`;
  });

  // Segment counter (Twilio-spec) derived from the live textarea content.
  const segInfo = computed(() => SMS.calcSegments(msgContent.value));
  const segSuggestions = computed(() => {
    const info = segInfo.value;
    if (info.segments < 2) return [];
    return SMS.suggestReduction(msgContent.value, info);
  });
  const segCountClass = computed(() => {
    const s = segInfo.value.segments;
    return s >= 3
      ? "text-brand-pink"
      : s === 2
      ? "text-amber-600 dark:text-amber-400"
      : "";
  });
  const segRemainingClass = computed(() =>
    segInfo.value.remaining <= 10 ? "text-brand-pink" : ""
  );
  const charCountLabel = computed(() => {
    const i = segInfo.value;
    return `${i.chars} chars · ${i.segments} segment${
      i.segments > 1 ? "s" : ""
    } · ${i.encoding}`;
  });

  // Diff against the live version (shown for variation-bearing types).
  const diffLines = computed(() => [
    { kind: "removed", text: SMS.EDITOR_LIVE_VERSION },
    { kind: "added", text: msgContent.value }
  ]);

  // Draft status of the currently selected variation.
  const editorVarIsDraft = computed(() => {
    const v = currentVar.value;
    return SMS.buildVariations(v.building).some(
      r =>
        r.timing === v.timing && r.weather === v.weather && r.status === "draft"
    );
  });

  function isVarActive(building, timing, weather) {
    const v = currentVar.value;
    return (
      v.building === building && v.timing === timing && v.weather === weather
    );
  }

  // ── Editor actions ────────────────────────────────────────────────────
  function selectVariation(building, timing, weather) {
    currentVar.value = { building, timing, weather };
    const key = `${building}/${timing}/${weather}`;
    msgContent.value =
      SMS.VARIATION_CONTENT[key] ||
      SMS.VARIATION_CONTENT[SMS.DEFAULT_VARIATION_KEY];
  }

  function switchMsgType(type) {
    msgType.value = type;
    msgContent.value =
      SMS.MSG_CONTENT_BY_TYPE[type] || SMS.MSG_CONTENT_BY_TYPE["heads-up"];
  }

  function toggleGroup(id) {
    openGroups.value = { ...openGroups.value, [id]: !openGroups.value[id] };
  }

  // Insert a variable token at the cursor, then write back through reactive
  // state so v-model and the segment counter stay in sync.
  function insertVar(v) {
    const ta = editorTextarea.value;
    if (!ta) {
      msgContent.value += v;
      return;
    }
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    msgContent.value =
      msgContent.value.slice(0, s) + v + msgContent.value.slice(e);
    nextTick(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = s + v.length;
    });
  }

  let savedTimer;
  let copiedTimer;
  function saveDraft() {
    editorSaved.value = true;
    clearTimeout(savedTimer);
    savedTimer = setTimeout(() => {
      editorSaved.value = false;
    }, 1500);
  }
  function copyToSimilar() {
    editorCopied.value = true;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => {
      editorCopied.value = false;
    }, 2000);
  }

  return {
    msgType,
    currentVar,
    msgContent,
    openGroups,
    editorSaved,
    editorCopied,
    editorTextarea,
    messageTypes,
    buildings,
    editorVars,
    editorVersions,
    variationsFor,
    currentMsgType,
    hasVariations,
    editorTitle,
    variationTitle,
    draftLabel,
    segInfo,
    segSuggestions,
    segCountClass,
    segRemainingClass,
    charCountLabel,
    diffLines,
    editorVarIsDraft,
    isVarActive,
    selectVariation,
    switchMsgType,
    toggleGroup,
    insertVar,
    saveDraft,
    copyToSimilar
  };
}
